import React, { useState, useEffect, useContext } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useMediaQuery } from '@material-ui/core';
import api from '../../services/api';
import { AuthContext } from '../../context/Auth/AuthContext';
import { toast } from 'react-toastify';
import { i18n } from '../../translate/i18n';
import { useHistory } from 'react-router-dom';
import { setKanbanLaneOrder, getKanbanLaneOrder } from '../../services/companyKanbanService';
import { Button, TextField, Paper, FormControl, InputLabel, Select, IconButton, Tooltip, Collapse } from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import AddIcon from '@material-ui/icons/Add';
import SearchIcon from '@material-ui/icons/Search';
import { format } from 'date-fns';
import { Can } from '../../components/Can';
import MainContainer from '../../components/MainContainer';
import MainHeader from '../../components/MainHeader';
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper';
import Title from '../../components/Title';
import KanbanBoard from './KanbanBoard';

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    display: 'flex',
    padding: theme.spacing(1),
    overflowX: 'auto',
    ...theme.scrollbarStyles,
    borderRadius: '10px',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0.5),
      borderRadius: 8,
    },
  },
  button: {
    borderRadius: '10px',
    flexShrink: 0,
  },
  dateInput: {
    '& .MuiOutlinedInput-root': { borderRadius: '10px' },
    flex: 1,
    minWidth: 130,
  },
  sortSelect: {
    minWidth: 150,
    '& .MuiOutlinedInput-root': { borderRadius: '10px' },
    [theme.breakpoints.down('xs')]: {
      minWidth: 0,
      flex: 1,
    },
  },
  mobileControls: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    width: '100%',
    padding: theme.spacing(0, 1, 1),
  },
  mobileRow: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
}));

const Kanban = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const { user, socket } = useContext(AuthContext);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [tags, setTags] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [lanes, setLanes] = useState([]);
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const queueIds = user.queues.map(queue => queue.UserQueue.queueId);

  const [sortOrder, setSortOrder] = useState(() => {
    return localStorage.getItem('sortOrder') || 'ticketNumber';
  });

  const [laneOrder, setLaneOrder] = useState(null);
  const [loadingLaneOrder, setLoadingLaneOrder] = useState(true);

  useEffect(() => {
    localStorage.setItem('sortOrder', sortOrder);
  }, [sortOrder]);

  // Carregar ordem das lanes do banco de dados
  useEffect(() => {
    const loadLaneOrder = async () => {
      try {
        const savedOrder = await getKanbanLaneOrder();
        setLaneOrder(savedOrder);
      } catch (error) {
        console.error('Erro ao carregar ordem das lanes:', error);
      } finally {
        setLoadingLaneOrder(false);
      }
    };

    if (user && user.id) {
      loadLaneOrder();
    }
  }, [user]);

  useEffect(() => {
    fetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTags = async () => {
    try {
      const response = await api.get('/tag/kanban/');
      const fetchedTags = response.data.lista || [];
      setTags(fetchedTags);
      fetchTickets(fetchedTags);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchTickets = async (fetchedTags = tags) => {
    try {
      const { data } = await api.get('/ticket/kanban', {
        params: {
          queueIds: JSON.stringify(queueIds),
          startDate: startDate,
          endDate: endDate,
        },
      });
      setTickets(data.tickets);
      organizeLanes(fetchedTags, data.tickets);
    } catch (err) {
      console.log(err);
      setTickets([]);
    }
  };

  useEffect(() => {
    const companyId = user.companyId;

    const onAppMessage = data => {
      if (['create', 'update', 'delete'].includes(data.action)) {
        fetchTickets();
      }
    };

    socket.on(`company-${companyId}-ticket`, onAppMessage);
    socket.on(`company-${companyId}-appMessage`, onAppMessage);

    return () => {
      socket.off(`company-${companyId}-ticket`, onAppMessage);
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, user.companyId]);

  const handleSearchClick = () => {
    fetchTickets();
  };

  const handleStartDateChange = event => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = event => {
    setEndDate(event.target.value);
  };

  const updateTicket = updatedTicket => {
    setTickets(prevTickets =>
      prevTickets.map(ticket =>
        ticket.id === updatedTicket.id ? updatedTicket : ticket
      )
    );
  };

  const getOpportunityValue = (ticket) => {
    const customFields = ticket.contact.extraInfo || [];
    const valueField = customFields.find(field => field.name === 'valor');
    const opportunityValue = valueField ? parseFloat(valueField.value) : 0;
    return opportunityValue;
  };

  const organizeLanes = (fetchedTags = tags, fetchedTickets = tickets) => {
    const sortedTickets = [...fetchedTickets];

    if (sortOrder === 'ticketNumber') {
      sortedTickets.sort((a, b) => a.id - b.id);
    } else if (sortOrder === 'lastMessageTime') {
      sortedTickets.sort(
        (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
      );
    } else if (sortOrder === 'valorDesc') {
      sortedTickets.sort((a, b) => {
        const valorA = getOpportunityValue(a);
        const valorB = getOpportunityValue(b);
        return valorB - valorA;
      });
    }

    const defaultTickets = sortedTickets.filter(
      ticket => ticket.tags.length === 0
    );

    const lanesData = [
      {
        id: 'lane0',
        title: i18n.t('tagsKanban.laneDefault'),
        tickets: defaultTickets,
        color: '#757575',
      },
      ...fetchedTags.map(tag => {
        const taggedTickets = sortedTickets.filter(ticket =>
          ticket.tags.some(t => t.id === tag.id)
        );
        return {
          id: tag.id.toString(),
          title: tag.name,
          tickets: taggedTickets,
          color: tag.color || '#757575',
        };
      }),
    ];

    // Aplicar ordem personalizada se existir
    if (laneOrder && laneOrder.length > 0) {
      const orderedLanes = [];
      const laneMap = new Map(lanesData.map(lane => [lane.id, lane]));
      
      // Adicionar lanes na ordem salva
      laneOrder.forEach(laneId => {
        if (laneMap.has(laneId)) {
          orderedLanes.push(laneMap.get(laneId));
          laneMap.delete(laneId);
        }
      });
      
      // Adicionar lanes restantes (novas lanes que não estavam na ordem salva)
      laneMap.forEach(lane => orderedLanes.push(lane));
      
      setLanes(orderedLanes);
    } else {
      setLanes(lanesData);
    }
  };

  useEffect(() => {
    if (!loadingLaneOrder) {
      organizeLanes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags, tickets, sortOrder, laneOrder, loadingLaneOrder]);

  const handleCardMove = async (ticketId, targetLaneId) => {
    ticketId = parseInt(ticketId, 10);
    try {
      await api.delete(`/ticket-tags/${ticketId}`);

      if (targetLaneId !== 'lane0') {
        await api.put(`/ticket-tags/${ticketId}/${targetLaneId}`);
        toast.success('Tag actualizada con éxito');
      } else {
        toast.success('Tag eliminada del ticket');
      }

      fetchTickets();
    } catch (err) {
      console.log(err);
    }
  };

  const handleAddColumnClick = () => {
    history.push('/tagsKanban');
  };

  const handleSortOrderChange = event => {
    setSortOrder(event.target.value);
  };

  const handleLaneReorder = async (sourceIndex, destinationIndex) => {
    // Verificar se o usuário é admin
    if (user.profile !== 'admin') {
      toast.error('Solo los administradores pueden reordenar las columnas');
      return;
    }

    const newLanes = Array.from(lanes);
    const [reorderedLane] = newLanes.splice(sourceIndex, 1);
    newLanes.splice(destinationIndex, 0, reorderedLane);
    
    setLanes(newLanes);
    
    // Salvar nova ordem no banco de dados
    const newLaneOrder = newLanes.map(lane => lane.id);
    setLaneOrder(newLaneOrder);
    
    try {
      await setKanbanLaneOrder(newLaneOrder);
      toast.success('Orden de columnas actualizada');
    } catch (error) {
      console.error('Error al guardar orden de columnas:', error);
      toast.error('Error al guardar el orden');
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t('Kanban')}</Title>
        <MainHeaderButtonsWrapper>
          {isMobile ? (
            /* ── MOBILE HEADER ── */
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <FormControl variant="outlined" size="small" style={{ flex: 1, minWidth: 140 }}>
                <InputLabel htmlFor="sort-order-select">Ordenar</InputLabel>
                <Select
                  native
                  value={sortOrder}
                  onChange={handleSortOrderChange}
                  label="Ordenar"
                  inputProps={{ name: 'sortOrder', id: 'sort-order-select' }}
                  style={{ borderRadius: 10 }}
                >
                  <option value="ticketNumber">Nº ticket</option>
                  <option value="lastMessageTime">Último msg.</option>
                  <option value="valorDesc">Valor ↓</option>
                </Select>
              </FormControl>
              <Tooltip title="Filtros de fecha">
                <IconButton
                  size="small"
                  onClick={() => setFiltersOpen(p => !p)}
                  style={{
                    border: `1px solid ${filtersOpen ? '#5B2D8E' : 'rgba(91,45,142,0.25)'}`,
                    borderRadius: 8,
                    color: filtersOpen ? '#5B2D8E' : undefined,
                    background: filtersOpen ? 'rgba(91,45,142,0.08)' : 'transparent',
                    padding: 6,
                  }}
                >
                  <FilterListIcon style={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Buscar">
                <IconButton
                  size="small"
                  onClick={handleSearchClick}
                  style={{
                    background: 'linear-gradient(135deg,#5B2D8E,#7C3AED)',
                    borderRadius: 8, padding: 6,
                  }}
                >
                  <SearchIcon style={{ fontSize: 18, color: '#fff' }} />
                </IconButton>
              </Tooltip>
              <Can
                role={user.profile}
                perform="dashboard:view"
                yes={() => (
                  <Tooltip title="Agregar columna">
                    <IconButton
                      size="small"
                      onClick={handleAddColumnClick}
                      style={{
                        background: 'linear-gradient(135deg,#5B2D8E,#7C3AED)',
                        borderRadius: 8, padding: 6,
                      }}
                    >
                      <AddIcon style={{ fontSize: 18, color: '#fff' }} />
                    </IconButton>
                  </Tooltip>
                )}
              />
            </div>
          ) : (
            /* ── DESKTOP HEADER ── */
            <>
              <FormControl variant="outlined" size="small" className={classes.sortSelect}>
                <InputLabel htmlFor="sort-order-select">Ordenar por</InputLabel>
                <Select
                  native
                  value={sortOrder}
                  onChange={handleSortOrderChange}
                  label="Ordenar por"
                  inputProps={{ name: 'sortOrder', id: 'sort-order-select' }}
                >
                  <option value="ticketNumber">Número de ticket</option>
                  <option value="lastMessageTime">Último mensaje</option>
                  <option value="valorDesc">Valor (mayor a menor)</option>
                </Select>
              </FormControl>
              <TextField
                label="Fecha de inicio"
                type="date"
                value={startDate}
                onChange={handleStartDateChange}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                className={classes.dateInput}
                size="small"
              />
              <TextField
                label="Fecha de fin"
                type="date"
                value={endDate}
                onChange={handleEndDateChange}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                className={classes.dateInput}
                size="small"
              />
              <Button variant="contained" color="primary" onClick={handleSearchClick} className={classes.button}>
                Buscar
              </Button>
              <Can
                role={user.profile}
                perform="dashboard:view"
                yes={() => (
                  <Button variant="contained" color="primary" onClick={handleAddColumnClick} className={classes.button}>
                    + Agregar columna
                  </Button>
                )}
              />
            </>
          )}
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {/* Date filters expandible en mobile */}
      {isMobile && (
        <Collapse in={filtersOpen}>
          <div className={classes.mobileControls}>
            <div className={classes.mobileRow}>
              <TextField
                label="Inicio"
                type="date"
                value={startDate}
                onChange={handleStartDateChange}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                className={classes.dateInput}
                size="small"
              />
              <TextField
                label="Fin"
                type="date"
                value={endDate}
                onChange={handleEndDateChange}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                className={classes.dateInput}
                size="small"
              />
            </div>
          </div>
        </Collapse>
      )}
      <Paper variant="outlined" className={classes.mainPaper}>
        <KanbanBoard
          lanes={lanes}
          onCardMove={handleCardMove}
          onLaneReorder={handleLaneReorder}
          updateTicket={updateTicket}
          isAdmin={user.profile === 'admin'}
        />
      </Paper>
    </MainContainer>
  );
};

export default Kanban;
